/**
 * Public analytics surface for the booking widget.
 *
 * Components should only ever import from this module — never from
 * `./segment` directly. This keeps the implementation swappable and the
 * event catalog (see `./events.ts`) the single source of truth.
 *
 * Tracking is best-effort and never throws: a missing write key, Do-Not-Track,
 * or a transport failure all degrade to a silent no-op.
 */

import { identifySegment, resetSegment, trackSegment } from './segment.ts';
import type { BookingType, EventName, EventProperties } from './events.ts';

export type { EventName, EventProperties, BookingType } from './events.ts';

/**
 * Normalize a widget-state `bookingType` (optional, looser string union) into
 * the analytics `BookingType`, defaulting to `'restaurant'` before a flow has
 * been chosen. Keeps `booking_type` consistent across every event without each
 * call site repeating the `?? 'restaurant'` fallback.
 */
export function resolveBookingType(bookingType?: string | null): BookingType {
  return bookingType === 'event' || bookingType === 'custom_request'
    ? bookingType
    : 'restaurant';
}

/**
 * Fire a typed widget funnel event.
 *
 * Properties are passed through as-is — callers are responsible for keeping
 * PII out (we report structural fields only: ids, types, booleans).
 */
export function track<E extends EventName>(event: E, properties: EventProperties[E]): void {
  trackSegment(event, properties as unknown as Record<string, unknown>);
}

/**
 * Associate the session with a member once their identity is known (e.g. right
 * after phone verification). Links the anonymous funnel events fired earlier in
 * the session to the member in every downstream destination, and tags every
 * later event with their id. Best-effort and never throws.
 *
 * `traits` are standard profile fields (name, email, phone) — the same ones the
 * member app sends on identify. Keep transient/URL-borne values out.
 */
export function identify(userId: string, traits?: Record<string, unknown>): void {
  identifySegment(userId, traits);
}

/**
 * Drop the current member's identity and start a fresh anonymous session. Call
 * when the widget clears its auth (e.g. re-verification on a shared browser) so
 * the next person's pre-verification funnel isn't attributed to the previous
 * member. Best-effort and never throws.
 */
export function reset(): void {
  resetSegment();
}
